


























































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import MiraInsurerDecisionDialogComponent, { MiraInsurerDecisionResult } from "@/components/admin/mira/MiraInsurerDecisionDialog.vue";
import MiraInsurerSuggestionDismissDialogComponent from "@/components/admin/mira/MiraInsurerSuggestionDismissDialog.vue";
import MiraInsurerDialogComponent from "@/components/admin/mira/MiraInsurerDialog.vue";
import {
  MiraInsurer,
  MiraInsurerList,
  MiraInsurerSuggestion,
  UnresolvedInsurerGroup,
  UnresolvedInsurerList,
} from "@/data/models/MiraInsurerAdministration";
import {
  decideUnresolvedInsurers,
  listMiraInsurers,
  listUnresolvedInsurers,
} from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_ALL_DEVICES_EDIT_ICON, COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  filterUnresolvedGroups,
  groupsWithSuggestion,
  insurerErrorMessage,
  newInsurerForm,
  normaliseInsurerName,
} from "@/utils/MiraInsurerAdministration";
import { themed } from "@/utils/Theme";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** The views: the groups still open, those with Mira's suggestion (#2610125), and the spellings decided "Kein Versicherer". */
const REVIEW_VIEWS = ["OPEN", "SUGGESTED", "NOT_INSURER"] as const;
type ReviewView = typeof REVIEW_VIEWS[number];

/** How many further spellings a row names before "und n weitere". */
const SPELLINGS_SHOWN = 3;

/**
 * The insurer names Mira could not resolve (Track #2610040 P3), grouped (legal forms and "Versicherung"
 * words dropped), largest first: a group is given to an insurer, whose spellings it then joins, or
 * decided "Kein Versicherer". A decided group leaves this list at once and reaches Mira's figures with
 * the next nightly run. The "Kein Versicherer" view lists the spellings decided so, each one releasable.
 *
 * Mira's suggestions (#2610125), made in the monthly session: a group shows its suggestion with a tick
 * (ÜBERNEHMEN: the usual dialog opens filled in, nothing is saved before its button) and a cross
 * (VERWERFEN, with a reason). The "Mit Vorschlag" view shows only those groups.
 *
 * **Not the access boundary**: the callables repeat the portal and `MANAGE_CLIENTS` checks.
 */
@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "decision-dialog": MiraInsurerDecisionDialogComponent,
    "suggestion-dismiss-dialog": MiraInsurerSuggestionDismissDialogComponent,
    "insurer-dialog": MiraInsurerDialogComponent,
  },
})
export default class MiraInsurerReviewComponent extends Vue {
  @Ref("decisionDialog")
  decisionDialog!: MiraInsurerDecisionDialogComponent;

  @Ref("insurerDialog")
  insurerDialog!: MiraInsurerDialogComponent;

  @Ref("suggestionDismissDialog")
  suggestionDismissDialog!: MiraInsurerSuggestionDismissDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  allGroups: UnresolvedInsurerGroup[] = [];
  insurers: MiraInsurer[] = [];
  allNotInsurers: string[] = [];
  loadedAt: string | null = null;
  droppedTexts = 0;
  /** Epoch ms of the monthly import the suggestions come from; a dismissal sends it back. */
  suggestionsAt: number | null = null;
  view: ReviewView = "OPEN";
  search = "";
  isLoading = true;
  loadError = false;

  readonly views = REVIEW_VIEWS;

  get groups(): UnresolvedInsurerGroup[] {
    const groups = this.view === "SUGGESTED" ? groupsWithSuggestion(this.allGroups) : this.allGroups;
    return filterUnresolvedGroups(groups, this.search);
  }

  get notInsurers(): string[] {
    const term = normaliseInsurerName(this.search);
    return term ? this.allNotInsurers.filter((spelling) => spelling.includes(term)) : this.allNotInsurers;
  }

  get rowCount(): number {
    return this.view === "NOT_INSURER" ? this.notInsurers.length : this.groups.length;
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rowCount > 0;
  }

  get loadedAtLabel(): string | null {
    return this.loadedAt ? formatDateToDayMonthYear(new Date(this.loadedAt)) ?? null : null;
  }

  get suggestionsAtLabel(): string | null {
    return this.suggestionsAt ? formatDateToDayMonthYear(new Date(this.suggestionsAt)) ?? null : null;
  }

  get activeInsurers(): MiraInsurer[] {
    return this.insurers.filter((insurer) => !insurer.retired);
  }

  get rowIconColor(): string {
    return COLOR_ALL_DEVICES_EDIT_ICON;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  hoverStyle(hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    return background ? `background-color: ${background}` : "";
  }

  viewName(view: ReviewView): string {
    return this.$t(`admin.miraInsurerReview.views.${view}`).toString();
  }

  /** The group's other spellings, the most used first, then how many more. */
  moreSpellings(group: UnresolvedInsurerGroup): string {
    const others = group.spellings.slice(1);
    const shown = others.slice(0, SPELLINGS_SHOWN).map((spelling) => spelling.text).join(", ");
    return others.length > SPELLINGS_SHOWN
      ? this.$t("admin.miraInsurerReview.moreSpellings", [shown, others.length - SPELLINGS_SHOWN]).toString()
      : shown;
  }

  /** "Vorschlag: Allianz (ALLI)", "Vorschlag: neuer Versicherer Aviva (AVIV)", "Vorschlag: Kein Versicherer". */
  suggestionText(suggestion: MiraInsurerSuggestion): string {
    if (suggestion.target === "NOT_INSURER") return this.$t("admin.miraInsurerReview.suggestion.notInsurer").toString();
    if (suggestion.target === "NEW_INSURER") {
      return this.$t("admin.miraInsurerReview.suggestion.newInsurer", [suggestion.shortName, suggestion.key]).toString();
    }
    const insurer = this.insurers.find((entry) => entry.key === suggestion.key);
    return this.$t("admin.miraInsurerReview.suggestion.insurer", [insurer?.shortName ?? suggestion.key, insurer?.code ?? suggestion.key]).toString();
  }

  onViewChanged(view: ReviewView) {
    this.view = view;
  }

  onSearch(value: string | null) {
    this.search = value ?? "";
  }

  async decide(group: UnresolvedInsurerGroup) {
    await this.applyDecision(group, await this.decisionDialog.show(group, this.activeInsurers));
  }

  /** ÜBERNEHMEN (#2610125): the usual dialog, filled in from the suggestion. */
  async takeOver(group: UnresolvedInsurerGroup) {
    const suggestion = group.suggestion;
    if (!suggestion) return;
    if (suggestion.target === "NEW_INSURER") {
      await this.applyDecision(group, { kind: "NEW_INSURER", texts: suggestion.texts });
      return;
    }
    const preselection = { kind: suggestion.target, key: suggestion.key, texts: suggestion.texts };
    await this.applyDecision(group, await this.decisionDialog.show(group, this.activeInsurers, preselection));
  }

  /** What Gruppe zuordnen decided, or a new insurer still to create; null when it was closed. */
  private async applyDecision(group: UnresolvedInsurerGroup, decided: MiraInsurerDecisionResult | null) {
    if (decided?.kind === "NEW_INSURER") {
      // #2610125: one save creates the insurer with the ticked spellings; closed without saving, nothing changed.
      // A group whose suggestion is a new insurer brings its key and names.
      const form = newInsurerForm(decided.texts, this.insurers.map((insurer) => insurer.code), group.suggestion ?? null);
      const created = await this.insurerDialog.show(null, form);
      if (!created || created === "CHANGED_MEANWHILE") return;
      this.insurers = [...this.insurers, created];
    }
    if (!decided) return;
    // Gone from the review at once; IRES Cloud leaves it out of the next list too.
    this.allGroups = this.allGroups.filter((entry) => entry.groupKey !== group.groupKey);
    if (decided.kind === "NOT_INSURER") {
      // Stored normalised, as IRES Cloud stores them.
      this.allNotInsurers = [...new Set([...this.allNotInsurers, ...decided.texts.map(normaliseInsurerName)])].sort();
    }
    // Spellings left unticked come back, and a suggested new insurer now created shows as that insurer elsewhere.
    this.refreshQuietly();
  }

  /** VERWERFEN (#2610125): asks why, then hides the suggestion in place. */
  async dismissSuggestion(group: UnresolvedInsurerGroup) {
    const suggestion = group.suggestion;
    if (!suggestion) return;
    const summary = `${suggestion.texts.join(", ")}: ${this.suggestionText(suggestion)}`;
    const result = await this.suggestionDismissDialog.show(suggestion, summary, this.suggestionsAt);
    if (result === "DISMISSED") {
      this.allGroups = this.allGroups.map((entry) => (entry.groupKey === group.groupKey ? { ...entry, suggestion: undefined } : entry));
      // Another spelling of the group may still carry a different suggestion.
      this.refreshQuietly();
    } else if (result === "RELOAD") {
      this.refreshQuietly();
    }
  }

  async release(spelling: string) {
    const confirmed = await this.confirmDialog.show({
      title: this.$t("admin.miraInsurerReview.releaseConfirm.title").toString(),
      message: this.$t("admin.miraInsurerReview.releaseConfirm.message", [spelling]).toString(),
      acceptMessage: this.$t("admin.miraInsurerReview.release").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (!confirmed) return;
    try {
      await decideUnresolvedInsurers({ kind: "RELEASE", texts: [spelling] });
      this.allNotInsurers = this.allNotInsurers.filter((entry) => entry !== spelling);
      SnackbarModule.showSuccess({ key: "admin.miraInsurerReview.released" });
    } catch (error) {
      SnackbarModule.showError(insurerErrorMessage(error));
    }
  }

  private applyLists(unresolved: UnresolvedInsurerList, list: MiraInsurerList) {
    this.allGroups = unresolved.groups;
    this.loadedAt = unresolved.loadedAt;
    this.droppedTexts = unresolved.droppedTexts;
    this.suggestionsAt = unresolved.suggestionsAt;
    this.insurers = list.insurers;
    this.allNotInsurers = list.notInsurers;
  }

  /** Reads the review again behind the rows shown, never with the spinner: a save refreshes in place. */
  async refreshQuietly() {
    try {
      const [unresolved, list] = await Promise.all([listUnresolvedInsurers(), listMiraInsurers()]);
      this.applyLists(unresolved, list);
    } catch {
      // Swallowed on purpose: the rows shown stay as they are, and the refresh button reloads them.
    }
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;
    try {
      const [unresolved, list] = await Promise.all([listUnresolvedInsurers(), listMiraInsurers()]);
      this.applyLists(unresolved, list);
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.miraInsurerReview.title");
    this.load();
  }
}
